/* ============================================================================
   Studio team — teammates invited from the Projects list who run the whole
   Project Manager module: their code signs them in as a Site Admin (create
   projects, enter every project room). Synced to D1 as one "meta" aggregate
   so the roster reaches every device.
   ========================================================================== */
import { useEffect, useState } from "react";
import { STUDIO, APP_URL, uid } from "./store";
import { uniqueAccessCode } from "./pmStore";
import { notify } from "./notify";
import { STUDIO_ROLE_LABEL, STUDIO_ROLE_BLURB } from "./studioRoles";
import type { StudioMember, StudioRole } from "./types";

const KEY = "atelier.studioteam.v1";
export const STUDIO_TEAM_AGG_ID = "studio-team";

type State = { members: StudioMember[] };

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { members: [] };
}

let state: State = load();
const subs = new Set<() => void>();

function commit(next: State) {
  state = next;
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
  changeListener?.(asAggregate());
}

export function useStudioTeam(): StudioMember[] {
  const [, force] = useState(0);
  useEffect(() => {
    const f = () => force((n) => n + 1);
    subs.add(f);
    return () => { subs.delete(f); };
  }, []);
  return state.members;
}
export function getStudioTeam() { return state.members; }

/* --- sync hooks --------------------------------------------------------- */
export type StudioTeamAggregate = { id: string; members: StudioMember[] };
function asAggregate(): StudioTeamAggregate { return { id: STUDIO_TEAM_AGG_ID, members: state.members }; }
let changeListener: ((a: StudioTeamAggregate) => void) | null = null;
export function onStudioTeamChange(f: (a: StudioTeamAggregate) => void) { changeListener = f; }
export function getStudioTeamAggregates(): StudioTeamAggregate[] { return [asAggregate()]; }
export function replaceStudioTeamAggregates(aggs: { id: string }[]) {
  const row = aggs.find((a) => a.id === STUDIO_TEAM_AGG_ID) as StudioTeamAggregate | undefined;
  if (!row) return;
  state = { members: row.members ?? [] };
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* quota */ }
  subs.forEach((f) => f());
}

/* --- actions ------------------------------------------------------------ */
export const studioTeamActions = {
  invite(name: string, email: string, role: StudioRole = "site-admin"): StudioMember {
    const code = uniqueAccessCode("ST", undefined, "TEAM", state.members.map((m) => m.accessCode));
    const m: StudioMember = { id: uid("st-"), name, email, role, accessCode: code, invitedAt: new Date().toISOString(), status: "invited" };
    commit({ members: [...state.members, m] });
    void notify([email],
      `You're on the ${STUDIO.name} studio team`,
      `${STUDIO.name} ${STUDIO.unit}: ${STUDIO.manager} added you to the studio team as ${STUDIO_ROLE_LABEL[role]} — ${STUDIO_ROLE_BLURB[role].toLowerCase()}. Sign in with your personal access code ${code} at ${APP_URL}.`);
    return m;
  },
  /** Change what a teammate's code opens — applies at their next sign-in. */
  setRole(id: string, role: StudioRole) {
    commit({ members: state.members.map((x) => (x.id === id ? { ...x, role } : x)) });
  },
  reissueCode(id: string): string | null {
    const m = state.members.find((x) => x.id === id);
    if (!m) return null;
    const code = uniqueAccessCode("ST", undefined, "TEAM", state.members.map((x) => x.accessCode));
    commit({ members: state.members.map((x) => (x.id === id ? { ...x, accessCode: code } : x)) });
    void notify([m.email],
      "Your access code was updated",
      `${STUDIO.name} ${STUDIO.unit}: your studio-team access code is now ${code}. The previous code no longer works. Sign in at ${APP_URL}.`);
    return code;
  },
  remove(id: string) {
    commit({ members: state.members.filter((x) => x.id !== id) });
  },
  markActive(id: string) {
    const m = state.members.find((x) => x.id === id);
    if (!m) return;
    const stamp = new Date().toISOString();
    // throttle: one write per half hour keeps sync chatter down
    if (m.status === "active" && m.lastActive && Date.now() - Date.parse(m.lastActive) < 30 * 60 * 1000) return;
    commit({ members: state.members.map((x) => (x.id === id ? { ...x, status: "active", lastActive: stamp } : x)) });
  },
};
